import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Copy, ExternalLink, Share2, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EnrollmentCodeDisplay = () => {
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState({ code: false, link: false });

  useEffect(() => {
    fetchEnrollmentInfo();
  }, []);

  const fetchEnrollmentInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tenant/enrollment-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnrollmentData(response.data);
    } catch (error) {
      console.error('Error fetching enrollment info:', error);
      toast.error('Erreur lors du chargement des informations d\'inscription');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [type]: true });
      toast.success('Copié dans le presse-papiers !');
      
      // Reset après 2 secondes
      setTimeout(() => {
        setCopied({ ...copied, [type]: false });
      }, 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  const shareLink = async () => {
    if (navigator.share && enrollmentData) {
      try {
        await navigator.share({
          title: `Inscription - ${enrollmentData.tenant_name}`,
          text: `Rejoignez ${enrollmentData.tenant_name} sur EduScope avec le code: ${enrollmentData.enrollment_code}`,
          url: enrollmentData.enrollment_link
        });
      } catch (error) {
        // L'utilisateur a annulé ou le navigateur ne supporte pas
        copyToClipboard(enrollmentData.enrollment_link, 'link');
      }
    } else if (enrollmentData) {
      copyToClipboard(enrollmentData.enrollment_link, 'link');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Chargement...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!enrollmentData) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-600">Impossible de charger les informations d'inscription</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Carte principale */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-blue-600" />
            Code d'Inscription de l'Établissement
          </CardTitle>
          <CardDescription>
            Partagez ces informations avec les nouveaux élèves et parents pour leur inscription
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Code d'inscription */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium text-gray-700">Code d'Inscription</Label>
              <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                {enrollmentData.allow_public_registration ? 'Inscriptions Ouvertes' : 'Inscriptions Fermées'}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex-1 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                <p className="text-3xl font-bold text-blue-900 text-center tracking-wider">
                  {enrollmentData.enrollment_code}
                </p>
              </div>
              <Button
                onClick={() => copyToClipboard(enrollmentData.enrollment_code, 'code')}
                variant="outline"
                size="icon"
                className="h-14 w-14"
              >
                {copied.code ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </Button>
            </div>
            
            <p className="text-xs text-gray-600">
              Les utilisateurs peuvent entrer ce code lors de leur inscription
            </p>
          </div>

          {/* Lien direct d'inscription */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-gray-700">Lien Direct d'Inscription</Label>
            
            <div className="flex items-center gap-2">
              <div className="flex-1 p-3 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <p className="text-sm text-gray-700 truncate font-mono">
                  {enrollmentData.enrollment_link}
                </p>
              </div>
              <Button
                onClick={() => copyToClipboard(enrollmentData.enrollment_link, 'link')}
                variant="outline"
                size="icon"
              >
                {copied.link ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5" />
                )}
              </Button>
              <Button
                onClick={() => window.open(enrollmentData.enrollment_link, '_blank')}
                variant="outline"
                size="icon"
              >
                <ExternalLink className="w-5 h-5" />
              </Button>
            </div>
            
            <p className="text-xs text-gray-600">
              Partagez ce lien par email, SMS ou sur votre site web
            </p>
          </div>

          {/* Bouton de partage */}
          <div className="pt-4 border-t">
            <Button
              onClick={shareLink}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Partager le Lien d'Inscription
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Comment Partager ?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Via Code</p>
                <p className="text-sm text-gray-600">
                  Communiquez le code <span className="font-mono font-bold">{enrollmentData.enrollment_code}</span> aux nouveaux élèves/parents. 
                  Ils devront l'entrer lors de leur inscription.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Via Lien Direct</p>
                <p className="text-sm text-gray-600">
                  Partagez le lien complet par email, SMS ou WhatsApp. L'établissement sera automatiquement pré-sélectionné.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Sur Documents Officiels</p>
                <p className="text-sm text-gray-600">
                  Ajoutez le code et le lien sur vos brochures, courriers d'admission et site web de l'établissement.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Label component simple
const Label = ({ className, children, ...props }) => (
  <label className={`block ${className || ''}`} {...props}>
    {children}
  </label>
);

export default EnrollmentCodeDisplay;
